import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Divider, Typography, MenuItem,
  TextField, Grid2 as Grid, Alert, FormControlLabel, Switch, CircularProgress,
} from "@mui/material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { vendorsApi, supplyTypesApi, type VendorDto, type AccountDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { AccountPicker } from "@/components/AccountPicker";

interface VendorFormProps {
  open: boolean;
  onClose: () => void;
  vendor?: VendorDto | null;
}

interface FormData {
  code: string;
  name: string;
  gstin?: string;
  pan?: string;
  isTaxRegistered: boolean;
  contactPerson?: string;
  phone?: string;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  paymentTerms?: string;
  creditDays?: number | null;
  isActive: boolean;
  supplyTypeId?: string;
  accountId?: string;
}

const empty: FormData = {
  code: "", name: "",
  gstin: "", pan: "", isTaxRegistered: false, contactPerson: "", phone: "", email: "",
  addressLine1: "", addressLine2: "", city: "", state: "", pincode: "", country: "",
  paymentTerms: "", creditDays: null, isActive: true, supplyTypeId: "", accountId: "",
};

interface VendorAddress {
  line1?: string; line2?: string; city?: string;
  state?: string; pincode?: string; country?: string;
}

function parseAddress(json?: string | null): VendorAddress {
  if (!json) return {};
  try { return JSON.parse(json) as VendorAddress; } catch { return {}; }
}

export function VendorForm({ open, onClose, vendor }: VendorFormProps) {
  const qc = useQueryClient();
  const isEdit = !!vendor;
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<FormData>({ defaultValues: empty });

  const isTaxRegistered = useWatch({ control, name: "isTaxRegistered" });
  const supplyTypesQuery = useQuery({
    queryKey: ["supply-types", "options"],
    queryFn: () => supplyTypesApi.list({ pageSize: 200 }),
    enabled: open,
  });
  const supplyTypes = supplyTypesQuery.data?.items ?? [];
  const [selectedAccount, setSelectedAccount] = useState<AccountDto | null>(null);

  useEffect(() => {
    const addr = parseAddress(vendor?.addressJson);
    reset(vendor ? {
      code: vendor.code,
      name: vendor.name,
      gstin: vendor.gstin ?? "",
      pan: vendor.pan ?? "",
      isTaxRegistered: vendor.isTaxRegistered ?? false,
      contactPerson: vendor.contactPerson ?? "",
      phone: vendor.phone ?? "",
      email: vendor.email ?? "",
      addressLine1: addr.line1 ?? "",
      addressLine2: addr.line2 ?? "",
      city: addr.city ?? "",
      state: addr.state ?? "",
      pincode: addr.pincode ?? "",
      country: addr.country ?? "",
      paymentTerms: vendor.paymentTerms ?? "",
      creditDays: vendor.creditDays ?? null,
      isActive: vendor.isActive,
      supplyTypeId: vendor.supplyTypeId ?? "",
      accountId: vendor.accountId ?? "",
    } : empty);
    setSelectedAccount(null); // AccountPicker resolves from initialId on next render
  }, [vendor, open, reset]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Vendor updated" : "Vendor created" },
    mutationFn: async (data: FormData) => {
      const address: VendorAddress = {
        line1: data.addressLine1 || undefined,
        line2: data.addressLine2 || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        pincode: data.pincode || undefined,
        country: data.country || undefined,
      };
      const hasAddress = Object.values(address).some(Boolean);
      const payload: Partial<VendorDto> = {
        code: data.code,
        name: data.name,
        isTaxRegistered: data.isTaxRegistered,
        isActive: data.isActive,
        gstin: data.gstin || undefined,
        pan: data.pan || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        contactPerson: data.contactPerson || undefined,
        paymentTerms: data.paymentTerms || undefined,
        creditDays: data.creditDays ?? undefined,
        addressJson: hasAddress ? JSON.stringify(address) : null,
        supplyTypeId: data.supplyTypeId || null,
        accountId: data.accountId || null,
      };
      if (isEdit && vendor) {
        await vendorsApi.update(vendor.id, payload);
        return vendor.id;
      }
      const result = await vendorsApi.create(payload);
      return result.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <ClosableDialogTitle onClose={onClose} disabled={isSubmitting}>
          {isEdit ? "Edit vendor" : "New vendor"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="code" control={control}
                rules={{ required: "Required", maxLength: 50 }}
                render={({ field }) => (
                  <TextField {...field} label="Code *" fullWidth size="small" disabled={isEdit}
                    error={!!errors.code} helperText={errors.code?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller
                name="name" control={control}
                rules={{ required: "Required", maxLength: 200 }}
                render={({ field }) => (
                  <TextField {...field} label="Name *" fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="isTaxRegistered" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Registered tax payer (GSTIN mandatory)"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="gstin" control={control}
                rules={{
                  required: isTaxRegistered ? "GSTIN is mandatory for a tax payer" : false,
                  pattern: {
                    value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                    message: "Invalid GSTIN format"
                  }
                }}
                render={({ field }) => (
                  <TextField {...field} label={isTaxRegistered ? "GSTIN *" : "GSTIN"} fullWidth size="small"
                    error={!!errors.gstin} helperText={errors.gstin?.message ?? "15-char GSTIN"} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="pan" control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                    message: "PAN must be 10 characters (e.g. ABCDE1234F)"
                  }
                }}
                render={({ field }) => (
                  <TextField {...field}
                    label="PAN" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    error={!!errors.pan} helperText={errors.pan?.message ?? "Optional · 10-char PAN"} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="contactPerson" control={control}
                render={({ field }) => <TextField {...field} label="Contact person" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="phone" control={control}
                render={({ field }) => <TextField {...field} label="Phone" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="email" control={control}
                rules={{ pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email" } }}
                render={({ field }) => (
                  <TextField {...field} type="email" label="Email" fullWidth size="small"
                    error={!!errors.email} helperText={errors.email?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="paymentTerms" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Payment terms" fullWidth size="small"
                    placeholder="e.g. Net 30" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="creditDays" control={control}
                rules={{ min: { value: 0, message: "Must be >= 0" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Credit days" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                    value={field.value ?? ""}
                    error={!!errors.creditDays} helperText={errors.creditDays?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="supplyTypeId" control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Supply type" fullWidth size="small"
                    helperText={supplyTypesQuery.isError ? "Could not load supply types" : "Medicine / Consumables / All"}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {supplyTypes.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </TextField>
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="accountId" control={control}
                render={({ field }) => (
                  <AccountPicker
                    value={selectedAccount}
                    initialId={field.value || undefined}
                    onChange={(v) => { setSelectedAccount(v); field.onChange(v?.id ?? ""); }}
                    helperText="Payable (AP) account head for this vendor"
                  />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="isActive" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Active"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Address</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="addressLine1" control={control}
                render={({ field }) => <TextField {...field} label="Address line 1" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="addressLine2" control={control}
                render={({ field }) => <TextField {...field} label="Address line 2" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="city" control={control}
                render={({ field }) => <TextField {...field} label="City" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="state" control={control}
                render={({ field }) => <TextField {...field} label="State" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="pincode" control={control}
                render={({ field }) => <TextField {...field} label="Pincode" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="country" control={control}
                render={({ field }) => <TextField {...field} label="Country" fullWidth size="small" />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained"
            disabled={isSubmitting || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
